import { Email as EmailIcon, VideoLibrary as VideoLibraryIcon } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { UserType, userTypeTexts } from 'redux/models/user'
import { DetailActions, DetailStoreStates } from '.'
import { FormControl, IdText, Paper } from './styles'

type OwnProps = {}

type DetailProps = OwnProps & DetailStoreStates & DetailActions

const Detail = (props: DetailProps) => {
  const navigate = useNavigate()
  const { readingWords, users, changeUserType, changeWordList } = props
  const { userId } = useParams<{ userId: string }>()

  const [preDeleting, setPreDeleting] = React.useState(false)

  const user = users.items.find((item) => item.id === userId)

  const onDeleteUser = () => {
    const { deleteUser } = props
    if (!user) return
    if (window.confirm('このユーザーを削除してもよろしいですか?')) deleteUser(user)
  }

  useEffect(() => {
    if (!users.loaded) props.loadUsers()
    if (!readingWords.list.loaded) props.loadWordLists()
  }, [])

  useEffect(() => {
    if (!preDeleting || users.deletion.deleting) {
      setPreDeleting(users.deletion.deleting)
      return
    }

    if (users.deletion.isSucceeded) {
      navigate('/users')
    } else {
      window.alert('エラーが発生したため削除できませんでした')
    }

    setPreDeleting(users.deletion.deleting)
  }, [preDeleting, users.deletion.deleting, users.deletion.isSucceeded])

  if (users.loading || !users.loaded) return <></>

  if (!user) return <Typography>ユーザが存在しません</Typography>

  return (
    <Container>
      <CssBaseline />
      <Paper>
        <Grid
          container
          direction="column"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'baseline',
            }}>
            <Grid>
              <Typography variant="h3" fontWeight="bold">
                {user.name}
              </Typography>
            </Grid>
            <Grid>
              <IdText component="span" variant="h6">
                {user.id}
              </IdText>
            </Grid>
          </Grid>
          <Grid container>
            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary={user.email} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VideoLibraryIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    user.numberOfMovies > 0
                      ? `${user.numberOfMovies}本（検証済：${user.numberOfVerifiedMovies}本）`
                      : `${user.numberOfMovies}本`
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
      <Paper>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Grid
            size={{
              md: 'grow',
              sm: 12,
              xs: 12,
            }}>
            <Typography variant="h6">ユーザの権限</Typography>
          </Grid>
          {users.userTypeChanging && (
            <Grid>
              <CircularProgress size={20} />
            </Grid>
          )}
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
            size={{
              md: 4,
              sm: 12,
              xs: 12,
            }}>
            <FormControl>
              <Select
                native
                variant="standard"
                value={user.type}
                disabled={users.userTypeChanging}
                onChange={(e) => changeUserType({ user, type: e.target.value })}>
                <option value={UserType.Admin}>{userTypeTexts[UserType.Admin]}</option>
                <option value={UserType.Researcher}>{userTypeTexts[UserType.Researcher]}</option>
                <option value={UserType.User}>{userTypeTexts[UserType.User]}</option>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      {readingWords.list.loaded && (
        <Paper>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center',
            }}>
            <Grid
              size={{
                md: 'grow',
                sm: 12,
                xs: 12,
              }}>
              <Typography variant="h6">使用するリスト</Typography>
            </Grid>
            {users.wordListChanging && (
              <Grid>
                <CircularProgress size={20} />
              </Grid>
            )}
            <Grid
              container
              sx={{
                justifyContent: 'flex-end',
              }}
              size={{
                md: 4,
                sm: 12,
                xs: 12,
              }}>
              <FormControl>
                <Select
                  native
                  variant="standard"
                  value={user.readingWordListId || ''}
                  disabled={users.wordListChanging}
                  onChange={(e) => changeWordList({ user, listId: e.target.value })}>
                  <option value="">デフォルト</option>
                  {readingWords.list.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Paper>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Grid
            size={{
              md: 'grow',
              sm: 12,
              xs: 12,
            }}>
            <Typography variant="h6">ユーザーの削除</Typography>
          </Grid>
          {users.deletion.deleting && (
            <Grid>
              <CircularProgress size={20} />
            </Grid>
          )}
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
            size={{
              md: 2,
              sm: 12,
              xs: 12,
            }}>
            <Button size="large" variant="contained" color="secondary" sx={{ m: 2 }} onClick={() => onDeleteUser()}>
              削除
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  )
}

export default Detail
